// hooks/useCartActions.ts
import { Productbtn } from "../../../../../../shared/services/interface";

interface CartActionsParams {
  selectedProductslist: Productbtn[];
  setSelectedProductslist: React.Dispatch<React.SetStateAction<Productbtn[]>>;
  setValueMap: React.Dispatch<React.SetStateAction<{ [id: string]: number }>>;
  setCondition: React.Dispatch<React.SetStateAction<number>>;
}

export const useCartActions = ({ selectedProductslist, setSelectedProductslist, setValueMap, setCondition }: CartActionsParams) => {
  // ✅ Add product
  const addProductToCart = (product: Productbtn) => {
    // Identify uniqueness by both _id and Amount
    const identifier = `${product._id.$oid}-${product.Amount}`;

    // Check if the exact product variant (same id + amount) is already added
    const existingProductIndex = selectedProductslist.findIndex((p) => `${p._id.$oid}-${p.Amount}` === identifier);

    if (existingProductIndex === -1) {
      // If not in the list, add it as a new product
      setSelectedProductslist((prevList) => [...prevList, product]);
      setValueMap((prevMap) => ({ ...prevMap, [identifier]: 1 }));
    } else {
      // If already present, increase its quantity
      setValueMap((prevMap) => ({ ...prevMap, [identifier]: (prevMap[identifier] || 1) + 1, }));
    }
  };

  // ✅ Remove product
  const removeProductFromCart = (id: string, amount: string) => {
    setSelectedProductslist((prevList) => prevList.filter((product) => product._id.$oid !== id));

    const identifier = `${id}-${amount}`;
    setValueMap((prevMap) => {
      const updatedMap = { ...prevMap };
      delete updatedMap[identifier];
      return updatedMap;
    });

    setCondition(0); // reset discount
  };

  // ✅ Update quantity
  const updateQuantity = (productId: string, amount: string, type: "add" | "subtract") => {
    const identifier = `${productId}-${amount}`;

    if (type === "add") {
      setValueMap((prevMap) => ({ ...prevMap, [identifier]: (prevMap[identifier] || 1) + 1 }));
    } else {
      setValueMap((prevMap) => {
        const updatedMap = { ...prevMap, [identifier]: Math.max((prevMap[identifier] || 1) - 1, 0) };
        if (updatedMap[identifier] === 0) {
          setSelectedProductslist((prevList) => prevList.filter((product) => product._id.$oid !== productId));
          setCondition(0);
        }
        return updatedMap;
      });
    }
  };

  return { addProductToCart, removeProductFromCart, updateQuantity };
};
